import { CURRENT_USER } from './homeModel';
import type { DevelopmentPlan, ProjectSummary } from './types';

export const HOME_SCENARIOS = ['0', 'partages', 'termines', 'personnels', 'mixte'] as const;
export type HomeScenario = typeof HOME_SCENARIOS[number];
export const SCENARIO_LABELS: Record<HomeScenario, string> = {
  '0': 'Vide', partages: 'Partagés uniquement', termines: 'Terminés uniquement',
  personnels: 'Personnels en cours', mixte: 'Mixte',
};

export function isHomeScenario(value: string | null): value is HomeScenario {
  return HOME_SCENARIOS.some((scenario) => scenario === value);
}

/** Même contenu et mêmes IDs ouvrables dans la V3 via ?projets=mixte. */
export function homeExamples(): ProjectSummary[] {
  const date = new Date().toLocaleDateString('fr-FR');
  const year = new Date().getFullYear();
  const month = String(new Date().getMonth() + 1).padStart(2, '0');
  return [
    { key: 'personnel', owner: CURRENT_USER, completed: false, name: 'Rennes — mon projet en cours' },
    { key: 'partage', owner: 'Alexandre Dupont', completed: false, name: 'Rennes — projet partagé en cours' },
    { key: 'termine-personnel', owner: CURRENT_USER, completed: true, name: 'Rennes — mon projet terminé' },
    { key: 'termine-partage', owner: 'Alexandre Dupont', completed: true, name: 'Rennes — projet partagé terminé' },
  ].map(({ key, owner, completed, name }) => ({
    id: `home-demo-${key}`, name, owner, completed,
    description: 'Exemple de démonstration de l’accueil Plan.',
    perimeterLabel: 'Rennes', communeNames: ['Rennes'], buildingCount: 0,
    snapshotDate: date, updatedAt: `${date} 09:00`, kind: key === 'partage' ? 'extension' : 'new',
    contract: { start: `${year}-${month}`, end: `${year + 20}-${month}` },
    planCount: key === 'partage' ? 5 : 0, parentId: null,
    members: owner === CURRENT_USER ? [] : [CURRENT_USER],
    memberRoles: owner === CURRENT_USER ? undefined : { [CURRENT_USER]: 'read' as const },
    archived: false,
  }));
}

/** Noms, progression et ratios de démonstration repris de plans.data.ts V2. */
export function developmentPlans(project: ProjectSummary, projectBuildings: number): DevelopmentPlan[] {
  if (project.id !== 'home-demo-partage') return [];
  const [datePart, timePart] = project.updatedAt.split(' ');
  const [day, month, year] = datePart.split('/').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  return ['Scénario de référence', 'Extension vers les écoles', 'Boucle du centre-ville', 'Raccordement des logements', 'Variante géothermie']
    .map((name, index) => {
      const date = new Date(Date.UTC(year, month - 1, day - index * 2, hours, minutes));
      const buildings = Math.max(1, Math.round(projectBuildings * Math.min(.95, .25 + index * .12)));
      return {
        id: `${project.id}-plan-${index + 1}`, name,
        updatedAt: date.toLocaleString('fr-FR', { timeZone: 'UTC', dateStyle: 'short', timeStyle: 'short' }),
        buildingCount: buildings, consumption: buildings * .085,
        networkLength: [3, 2, 1, 3, 2][index] >= 2 ? buildings * .012 : null,
      };
    });
}

export function inHomeScenario(project: ProjectSummary, scenario: HomeScenario): boolean {
  switch (scenario) {
    case '0': return false;
    case 'partages': return project.owner !== CURRENT_USER;
    case 'termines': return project.completed === true;
    case 'personnels': return project.owner === CURRENT_USER && !project.completed;
    case 'mixte': return true;
  }
}
